import type { ImportWizardStep } from "../import-wizard.js";
import { IMPORT_WIZARD_STEPS } from "../import-wizard.js";
import { Check, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";

const STEP_KEY: Record<ImportWizardStep, "file" | "mapping" | "review"> = {
  file: "file",
  map: "mapping",
  review: "review",
};

export function ImportContactsStepper({
  active,
  mapUnlocked,
  reviewUnlocked,
  onSelect,
}: {
  active: ImportWizardStep;
  mapUnlocked: boolean;
  reviewUnlocked: boolean;
  onSelect: (step: ImportWizardStep) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const activeIndex = IMPORT_WIZARD_STEPS.indexOf(active);

  return (
    <ol
      className="flex min-w-0 items-start"
      data-testid="contact-import-steps"
      aria-label={t("contacts.import.stepsLabel")}
    >
      {IMPORT_WIZARD_STEPS.map((step, index) => {
        const locked =
          (step === "map" && !mapUnlocked && active !== "map") ||
          (step === "review" && !reviewUnlocked && active !== "review");
        const complete = index < activeIndex;
        const current = step === active;
        const isLast = index === IMPORT_WIZARD_STEPS.length - 1;
        const connectorFilled = index < activeIndex;
        const label = t(`contacts.import.steps.${STEP_KEY[step]}`);
        const marker = (
          <span
            className={cn(
              "inline-grid size-6 shrink-0 place-items-center rounded-full text-[0.6875rem] font-semibold tabular-nums",
              current && "bg-accent text-accent-contrast",
              complete && !current && "bg-accent/15 text-accent",
              !current && !complete && !locked && "border border-line bg-surface text-muted",
              locked && "border border-line/80 bg-surface-muted text-subtle",
            )}
            aria-hidden="true"
          >
            {complete ? <Check size={12} strokeWidth={2.5} /> : index + 1}
          </span>
        );
        const className = cn(
          "flex min-h-11 min-w-0 flex-col items-center justify-center gap-1 rounded-md px-1 py-1 text-center",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          current ? "font-semibold text-accent" : "font-medium text-foreground",
          locked && "text-subtle",
        );
        const text = (
          <span className="text-[0.75rem] leading-snug">{label}</span>
        );

        return (
          <li
            key={step}
            className={cn("flex min-w-0 items-start", isLast ? "shrink-0" : "flex-1")}
          >
            {locked ? (
              <div className={className} aria-disabled="true">
                {marker}
                {text}
              </div>
            ) : (
              <button
                type="button"
                className={cn(className, "cursor-pointer hover:bg-surface-muted")}
                aria-current={current ? "step" : undefined}
                onClick={() => onSelect(step)}
              >
                {marker}
                {text}
              </button>
            )}
            {isLast ? null : (
              <span
                className="mt-3 flex min-w-4 flex-1 items-center px-1"
                aria-hidden="true"
                data-testid={`contact-import-step-connector-${step}`}
                data-filled={connectorFilled ? "true" : "false"}
              >
                <span
                  className={cn("h-px min-w-2 flex-1", connectorFilled ? "bg-accent/70" : "bg-line")}
                />
                <ChevronRight
                  size={12}
                  className={cn(
                    "shrink-0 rtl:-scale-x-100",
                    connectorFilled ? "text-accent" : "text-subtle",
                  )}
                />
                <span
                  className={cn("h-px min-w-2 flex-1", connectorFilled ? "bg-accent/70" : "bg-line")}
                />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
